import { useParams } from 'react-router'
import { useGetPosition } from '../../http/hooks.ts'
import Loader from '../../common/Loader'
import { IQuestion } from '../../services/nextpeakClient'
import { fromCamelCase, toCamelCase, toCapitalized } from '../Home/utils.ts'
import { Page } from '../../layout/Page.tsx'
import Card from '../../newComponents/Card/Card.tsx'
import CardHeader from '../../newComponents/Card/CardHeader.tsx'
import CardContent from '../../newComponents/Card/CardContent.tsx'
import { useNextPeakClient } from '../../services/nextpeakClient/clientProvider.tsx'
import { Heading1, Heading2 } from '../../newComponents/Headings/index.tsx'

const groupByProperties = (questions: IQuestion[]) => {
	return questions.reduce(
		(acc, question) => {
			const { name, subName } = question

			const lowerCaseName = toCamelCase(name.toLowerCase())
			const lowerCaseSubName = toCamelCase(subName.toLowerCase())

			// If the `name` group does not exist, create it
			if (!acc[lowerCaseName]) {
				acc[lowerCaseName] = {}
			}

			// If the `subName` group under `name` does not exist, create it
			if (!acc[lowerCaseName][lowerCaseSubName]) {
				acc[lowerCaseName][lowerCaseSubName] = []
			}

			// Push the question into the appropriate group
			acc[lowerCaseName][lowerCaseSubName].push(question)

			return acc
		},
		{} as Record<string, Record<string, IQuestion[]>>
	)
}

interface QuestionsProps {
	questions: IQuestion[]
}

const Questions = ({ questions }: QuestionsProps) => {
	const groupedQuestions = groupByProperties(questions)

	return (
		<div className="flex flex-col gap-4">
			{Object.entries(groupedQuestions).map(([name, subQuestions]) => (
				<Card key={name} useDefaultPadding={false}>
					<CardHeader>
						<Heading1>{toCapitalized(name)}</Heading1>
					</CardHeader>
					<CardContent>
						{Object.entries(subQuestions).map(([subName, questions]) => (
							<div key={subName} className="px-5 pt-4 sm:px-7.5">
								<Heading2>{toCapitalized(fromCamelCase(subName))}</Heading2>
								<ul>
									{questions.map((question) => (
										<li key={question.id} className="mb-4 dark:text-gray-400">
											{question.subordinateText}
										</li>
									))}
								</ul>
								<hr className="my-10 h-0.5 border-t-0 bg-neutral-100 dark:bg-white/10" />
							</div>
						))}
					</CardContent>
				</Card>
			))}
		</div>
	)
}

const PositionDetail = () => {
	const { positionId } = useParams()
	const client = useNextPeakClient()
	const { data, isLoading } = useGetPosition(client, {
		positionId: positionId!,
		includes: ['assessmentType', 'assessmentType.questions'],
	})

	return (
		<Page title="Position" breadcrumbs="Positions" description="Positions">
			{isLoading && <Loader />}
			{!isLoading && data && (
				<>
					<div className="flex flex-col">
						<p className="mb-7 dark:text-gray-400">{data.description}</p>
						<Questions questions={data.assessmentType?.questions!} />
					</div>
				</>
			)}
		</Page>
	)
}

export default PositionDetail
